/**
 * @fileoverview Mapeamento de variáveis do template Meta para valores do disparo.
 * Detecta `{{N}}` no body do template e oferece, para cada variável, um dropdown:
 *  - campos do contato (name, number, email, customField:X)
 *  - colunas das bases selecionadas (column:<Header>)
 *  - "Valor fixo" (fixed:<texto>) com input livre
 * Inclui prévia real (POST /mass-send/validate → sample.body + estimatedContacts
 * + errors) e envio de teste (POST /mass-send/test) para um número informado.
 */

import React, { useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { Eye, SendHorizontal, AlertTriangle } from "lucide-react";
import { toast } from "react-toastify";
import api from "../../../../services/api";
import toastError from "../../../../errors/toastError";
import { extractTemplateVars } from "../../../../utils/templateVars";

const BASE_FIELDS = [
  { value: "", label: "— Não mapeado —" },
  { value: "name", label: "Nome do contato" },
  { value: "number", label: "Número" },
  { value: "email", label: "Email" },
];

const FIXED_OPT = "__fixed__";

const extractVariables = extractTemplateVars;

const TemplateVariableMapper = ({
  selectedTemplate,
  mapping,
  onChange,
  baseColumns,
  previewContext,
}) => {
  const isDark = useTheme().palette.type === "dark";
  const W = (a) => (isDark ? `rgba(255,255,255,${a})` : `rgba(20,22,30,${a})`);

  const labelStyle = {
    fontSize: 11,
    fontWeight: 600,
    color: W(0.7),
    marginBottom: 4,
    display: "block",
  };
  const selectStyle = {
    width: "100%",
    padding: "7px 10px",
    borderRadius: 6,
    border: "1px solid " + W(0.08),
    background: W(0.03),
    color: W(0.9),
    fontSize: 12,
    outline: "none",
    boxSizing: "border-box",
    cursor: "pointer",
  };
  const inputStyle = { ...selectStyle, cursor: "text", marginTop: 6 };

  const [customNames, setCustomNames] = useState([]);
  const [loadingCustom, setLoadingCustom] = useState(false);

  const [preview, setPreview] = useState(null); // { valid, errors, estimatedContacts, sample }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [testing, setTesting] = useState(false);

  const body = selectedTemplate?.bodyText || "";
  const variables = useMemo(() => extractVariables(body), [body]);
  const columns = Array.isArray(baseColumns) ? baseColumns : [];

  useEffect(() => {
    if (variables.length === 0) return;
    let cancelled = false;
    setLoadingCustom(true);
    api
      .get("/contacts/custom-field-names")
      .then(({ data }) => {
        if (!cancelled) setCustomNames(Array.isArray(data?.names) ? data.names : []);
      })
      .catch(() => {
        if (!cancelled) setCustomNames([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingCustom(false);
      });
    return () => {
      cancelled = true;
    };
  }, [variables.length]);

  // Se o template mudou e o mapping tem chaves que não existem mais, limpa.
  useEffect(() => {
    const current = mapping || {};
    const keysInMapping = Object.keys(current);
    const extras = keysInMapping.filter((k) => !variables.includes(k));
    if (extras.length > 0) {
      const next = { ...current };
      extras.forEach((k) => delete next[k]);
      onChange(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variables.join(",")]);

  if (variables.length === 0) return null;

  const update = (key, value) => {
    const next = { ...(mapping || {}) };
    if (!value) delete next[key];
    else next[key] = value;
    onChange(next);
  };

  const labelFor = (field) => {
    if (field === "name") return "João Silva";
    if (field === "number") return "5511999998888";
    if (field === "email") return "joao@email.com";
    if (field && field.startsWith("customField:")) return `[${field.slice("customField:".length)}]`;
    if (field && field.startsWith("column:")) return `[${field.slice("column:".length)}]`;
    if (field && field.startsWith("fixed:")) return field.slice("fixed:".length) || "(fixo)";
    return "(sem valor)";
  };

  // Preview local instantânea (rótulos fictícios).
  const previewBody = variables.reduce((acc, k) => {
    return acc.split(`{{${k}}}`).join(labelFor((mapping || {})[k]));
  }, body);

  const options = [
    ...BASE_FIELDS,
    ...columns.map((h) => ({ value: `column:${h}`, label: `Coluna: ${h}` })),
    ...customNames.map((n) => ({ value: `customField:${n}`, label: `Campo personalizado: ${n}` })),
    { value: FIXED_OPT, label: "Valor fixo" },
  ];

  const ctx = previewContext || {};
  // Payload espelha o do disparo real: cada fonte manda o que o backend precisa
  // pra estimar (csv → contacts; filter → filter; list → segmentId). Sem isso a
  // prévia de csv/filter sempre mentia ("Nenhum contato no CSV" / 0 contatos).
  const buildPayload = () => ({
    whatsappId: ctx.whatsappId,
    templateName: ctx.templateName,
    language: ctx.language || (selectedTemplate && selectedTemplate.language),
    bodyText: ctx.bodyText || body,
    variableMapping: mapping || {},
    source: ctx.source,
    baseIds: ctx.baseIds,
    contacts: ctx.contacts,
    filter: ctx.filter,
    segmentId: ctx.segmentId,
    schedule: ctx.schedule,
  });

  const buildTestVariables = () => {
    const out = {};
    variables.forEach((k) => {
      const f = (mapping || {})[k] || "";
      if (f.startsWith("fixed:")) out[k] = f.slice("fixed:".length) || "exemplo";
      else if (f === "name") out[k] = "João Silva";
      else if (f === "number") out[k] = "5511999998888";
      else if (f === "email") out[k] = "exemplo@email.com";
      else if (f.startsWith("column:")) out[k] = f.slice("column:".length);
      else if (f.startsWith("customField:")) out[k] = f.slice("customField:".length);
      else out[k] = "exemplo";
    });
    return out;
  };

  const generatePreview = () => {
    setPreviewLoading(true);
    setPreview(null);
    api
      .post("/mass-send/validate", buildPayload())
      .then(({ data }) => setPreview(data))
      .catch((err) => {
        const msg = err?.response?.data?.error || "Falha ao gerar a prévia.";
        setPreview({ valid: false, errors: [msg], estimatedContacts: null, sample: null });
      })
      .finally(() => setPreviewLoading(false));
  };

  const sendTest = () => {
    if (!ctx.whatsappId || !ctx.templateName) {
      toast.error("Conecte a uma conexão e selecione um template antes de testar.");
      return;
    }
    const phone = window.prompt("Número para o teste (com DDI, ex: 5511999998888):");
    if (!phone || !phone.trim()) return;
    setTesting(true);
    api
      .post("/mass-send/test", {
        whatsappId: ctx.whatsappId,
        templateName: ctx.templateName,
        language: ctx.language || (selectedTemplate && selectedTemplate.language),
        bodyText: ctx.bodyText || body,
        variables: buildTestVariables(),
        number: phone.trim(),
      })
      .then(({ data }) => {
        if (data && data.success) toast.success("Mensagem de teste enviada!");
        else toast.info("Teste processado.");
      })
      .catch((err) => toastError(err))
      .finally(() => setTesting(false));
  };

  const previewErrors = (preview && preview.errors) || [];

  return (
    <div>
      <div
        style={{
          fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase",
          color: W(0.45), marginBottom: 8,
        }}
      >
        Variáveis do template
      </div>

      {variables.map((k) => {
        const current = (mapping || {})[k] || "";
        const isFixed = current.indexOf("fixed:") === 0;
        const selectValue = isFixed ? FIXED_OPT : current;
        return (
          <div key={k} style={{ marginBottom: 8 }}>
            <label style={labelStyle}>
              <span
                style={{
                  fontFamily: "monospace", color: "#a5b4fc", marginRight: 6, padding: "1px 5px",
                  borderRadius: 4, background: "rgba(99,102,241,0.15)", fontWeight: 700,
                }}
              >
                {`{{${k}}}`}
              </span>
              →
            </label>
            <select
              style={selectStyle}
              value={selectValue}
              onChange={(e) => {
                const v = e.target.value;
                if (v === FIXED_OPT) update(k, isFixed ? current : "fixed:");
                else update(k, v);
              }}
              disabled={loadingCustom}
            >
              {options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {isFixed && (
              <input
                style={inputStyle}
                placeholder="Texto fixo (ex: Promoção de julho)"
                value={current.slice("fixed:".length)}
                onChange={(e) => update(k, "fixed:" + e.target.value)}
              />
            )}
          </div>
        );
      })}

      {/* Preview local instantânea */}
      <div
        style={{
          marginTop: 10, padding: "8px 10px", borderRadius: 6, background: W(0.03),
          border: "1px dashed " + W(0.1), fontSize: 11, color: W(0.65),
          whiteSpace: "pre-wrap", lineHeight: 1.5,
        }}
      >
        <div
          style={{
            fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
            color: W(0.4), marginBottom: 4,
          }}
        >
          Prévia (rótulos)
        </div>
        {previewBody}
      </div>

      {/* Ações: prévia real + teste */}
      <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
        <div
          onClick={() => { if (!previewLoading) generatePreview(); }}
          style={{
            flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            padding: "8px 0", borderRadius: 8, cursor: previewLoading ? "wait" : "pointer",
            background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.25)",
            color: "#a5b4fc", fontSize: 11, fontWeight: 600,
          }}
        >
          {previewLoading ? <CircularProgress size={12} style={{ color: "#a5b4fc" }} /> : <Eye size={13} />}
          Prévia da mensagem
        </div>
        <div
          onClick={() => { if (!testing) sendTest(); }}
          style={{
            flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            padding: "8px 0", borderRadius: 8, cursor: testing ? "wait" : "pointer",
            background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.25)",
            color: "#4ade80", fontSize: 11, fontWeight: 600,
          }}
        >
          {testing ? <CircularProgress size={12} style={{ color: "#4ade80" }} /> : <SendHorizontal size={13} />}
          Enviar teste
        </div>
      </div>

      {/* Resultado da prévia real */}
      {preview && (
        <div style={{ marginTop: 10 }}>
          {typeof preview.estimatedContacts === "number" && (
            <div style={{ fontSize: 11, fontWeight: 600, color: "#34d399", marginBottom: 6 }}>
              {preview.estimatedContacts.toLocaleString("pt-BR")} contato
              {preview.estimatedContacts === 1 ? "" : "s"} estimado{preview.estimatedContacts === 1 ? "" : "s"}
            </div>
          )}
          {preview.sample && preview.sample.body && (
            <div
              style={{
                padding: "10px 12px", borderRadius: 6, background: "rgba(99,102,241,0.06)",
                border: "1px solid rgba(99,102,241,0.2)", fontSize: 12, lineHeight: 1.5,
                color: W(0.8), whiteSpace: "pre-wrap",
              }}
            >
              {preview.sample.body}
            </div>
          )}
          {previewErrors.length > 0 && (
            <div
              style={{
                marginTop: 6, padding: "8px 10px", borderRadius: 6, background: "rgba(239,68,68,0.08)",
                border: "1px solid rgba(239,68,68,0.22)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: "#f87171", marginBottom: 4 }}>
                <AlertTriangle size={12} /> Problemas encontrados
              </div>
              {previewErrors.map((e, i) => (
                <div key={i} style={{ fontSize: 10, color: W(0.7), lineHeight: 1.5 }}>
                  • {typeof e === "string" ? e : e.message || JSON.stringify(e)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TemplateVariableMapper;
